import { useState } from "react";
import { z } from "zod";
import { updateRideApi } from "services/auth/rideApis";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

// Zod schema for edit ride (all fields optional since users can update only what they want)
const editRideSchema = z.object({
  capacity: z
    .number()
    .int()
    .min(1, "Capacity must be at least 1")
    .max(20, "Capacity must not exceed 20")
    .optional(),
  totalFare: z
    .number()
    .int()
    .min(0, "Per head cost must be at least 0")
    .max(5000, "Per head cost must not exceed 5000")
    .optional(),
  vehicleType: z.enum(["CAR", "BIKE", "VAN", "AUTO"]).optional(),
  description: z.string().optional(),
});

type EditRideForm = {
  capacity: string;
  totalFare: string;
  vehicleType: "CAR" | "BIKE" | "VAN" | "AUTO";
  description: string;
};

interface RideDetails {
  id: string;
  capacity: number;
  date: string;
  totalFare: number;
  vehicleType: "CAR" | "BIKE" | "VAN" | "AUTO";
  description: string;
  [key: string]: any;
}

interface EditRideCardProps {
  rideDetails: RideDetails;
  setEdit: (value: boolean) => void;
}

export const EditRideCard = ({ rideDetails, setEdit }: EditRideCardProps) => {
  const [formData, setFormData] = useState<EditRideForm>({
    capacity: rideDetails.capacity.toString(),
    totalFare: rideDetails.totalFare.toString(),
    vehicleType: rideDetails.vehicleType || "CAR",
    description: rideDetails.description || "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof EditRideForm, string>>
  >({});
  const [loading, setLoading] = useState(false);

  const handleEditCardSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      // Convert string values to numbers for validation
      const validationData: any = {};

      if (formData.capacity) {
        validationData.capacity = parseInt(formData.capacity, 10);
      }
      if (formData.totalFare) {
        validationData.totalFare = parseInt(formData.totalFare, 10);
      }
      if (formData.vehicleType) {
        validationData.vehicleType = formData.vehicleType;
      }
      if (formData.description !== undefined) {
        validationData.description = formData.description;
      }

      // Validate form data
      editRideSchema.parse(validationData);

      setLoading(true);

      // Prepare data to send (only changed fields)
      const updateData: any = {};
      if (
        formData.capacity &&
        formData.capacity !== rideDetails.capacity.toString()
      ) {
        updateData.capacity = validationData.capacity;
      }
      if (
        formData.totalFare &&
        formData.totalFare !== rideDetails.totalFare.toString()
      ) {
        updateData.totalFare = validationData.totalFare;
      }
      if (formData.vehicleType !== rideDetails.vehicleType) {
        updateData.vehicleType = formData.vehicleType;
      }
      if (formData.description !== rideDetails.description) {
        updateData.description = formData.description || "";
      }

      const data = await updateRideApi(rideDetails.id, updateData);
      setLoading(false);
      if (data) {
        setEdit(false);
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        // Transform Zod errors into a readable format
        const fieldErrors: Partial<Record<keyof EditRideForm, string>> = {};
        err.issues.forEach((issue) => {
          const field = issue.path[0] as keyof EditRideForm;
          fieldErrors[field] = issue.message;
        });
        setErrors(fieldErrors);

        // Show first error as toast
        const firstIssue = err.issues[0];
        toast.error(firstIssue.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
      setLoading(false);
    } finally {
      if (Object.keys(errors).length === 0) {
        setLoading(false);
      }
    }
  };

  return (
    <Card className="w-full max-w-xl mx-auto my-6">
      <CardHeader className="pb-2 flex flex-row items-center gap-2">
        <button
          type="button"
          onClick={() => setEdit(false)}
          className="rounded-full border border-input p-2 bg-background hover:bg-accent transition-colors text-base flex items-center"
          aria-label="Back"
        >
          <FaArrowLeft />
        </button>
        <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
          Edit Ride
        </CardTitle>
      </CardHeader>
      <Separator className="my-1" />
      <CardContent className="flex flex-col gap-2 md:gap-3 text-zinc-800">
        <div className="text-sm text-zinc-500 mb-2">
          <strong>Note:</strong> Update only the fields you wish to change.
          Others will remain unchanged.
        </div>
        <form onSubmit={handleEditCardSave} className="flex flex-col gap-4">
          {/* capacity */}
          <div>
            <label
              htmlFor="capacity"
              className="block text-sm font-medium text-zinc-700 mb-1"
            >
              Capacity
            </label>
            <input
              type="number"
              id="capacity"
              name="capacity"
              value={formData.capacity}
              onChange={(e) => {
                setFormData({ ...formData, capacity: e.target.value });
                setErrors({ ...errors, capacity: undefined });
              }}
              min="1"
              max="20"
              className={`w-full px-3 py-2 border ${
                errors.capacity ? "border-red-500" : "border-zinc-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-amber-200 text-base`}
            />
            {errors.capacity && (
              <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>
            )}
          </div>

          {/* price */}
          <div>
            <label
              htmlFor="totalFare"
              className="block text-sm font-medium text-zinc-700 mb-1"
            >
              Per Head Cost
            </label>
            <input
              type="number"
              id="totalFare"
              name="totalFare"
              min="0"
              max="5000"
              value={formData.totalFare}
              onChange={(e) => {
                setFormData({ ...formData, totalFare: e.target.value });
                setErrors({ ...errors, totalFare: undefined });
              }}
              className={`w-full px-3 py-2 border ${
                errors.totalFare ? "border-red-500" : "border-zinc-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-amber-200 text-base`}
            />
            {errors.totalFare && (
              <p className="text-red-500 text-sm mt-1">{errors.totalFare}</p>
            )}
          </div>

          {/* vehicle type */}
          <div>
            <label
              htmlFor="vehicleType"
              className="block text-sm font-medium text-zinc-700 mb-1"
            >
              Vehicle Type
            </label>
            <Select
              value={formData.vehicleType}
              onValueChange={(value) => {
                setFormData({
                  ...formData,
                  vehicleType: value as "CAR" | "BIKE" | "VAN" | "AUTO",
                });
                setErrors({ ...errors, vehicleType: undefined });
              }}
            >
              <SelectTrigger
                className={errors.vehicleType ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select vehicle type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CAR">Car</SelectItem>
                <SelectItem value="BIKE">Bike</SelectItem>
                <SelectItem value="VAN">Van</SelectItem>
                <SelectItem value="AUTO">Auto</SelectItem>
              </SelectContent>
            </Select>
            {errors.vehicleType && (
              <p className="text-red-500 text-sm mt-1">{errors.vehicleType}</p>
            )}
          </div>

          {/* description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-zinc-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setErrors({ ...errors, description: undefined });
              }}
              className={`w-full px-3 py-2 border ${
                errors.description ? "border-red-500" : "border-zinc-300"
              } rounded-md focus:outline-none focus:ring-2 focus:ring-amber-200 text-base min-h-[80px] resize-none break-words whitespace-pre-wrap overflow-x-hidden`}
              style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col md:flex-row gap-2 md:justify-center mt-2 p-4">
        <Button
          type="submit"
          onClick={handleEditCardSave}
          size={"lg"}
          disabled={loading}
          className={`rounded-lg border px-4 py-2 hover:bg-amber-500 text-white font-medium transition-colors text-sm w-full md:w-fit ${
            loading ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </CardFooter>
    </Card>
  );
};
